import { useState, useEffect } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import WhatsAppSendModal from "../components/WhatsAppSendModal";
import { toast } from "sonner";
import { Plus, WhatsappLogo, Wrench } from "@phosphor-icons/react";
import { buildAMCVars } from "../utils/whatsappContext";

const AMCContracts = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState(null);
  const [wa, setWa] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/amc-contracts`);
      setItems(r.data || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editor.customer_name || !editor.start_date || !editor.end_date) { toast.error("Customer + dates required"); return; }
    try {
      await axios.post(`${API}/amc-contracts?requester_role=${user?.role || ""}&requester_user_id=${user?.user_id || ""}&requester_name=${encodeURIComponent(user?.name || "")}`, editor);
      toast.success("AMC created"); setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const openWa = (a, action) => {
    setWa({
      module: "amc",
      receiver: { name: a.customer_name, phone: a.customer_phone, type: "customer" },
      defaultAction: action,
      context: { variables: buildAMCVars(a) },
    });
  };

  const today = new Date().toISOString().slice(0, 10);
  const expiring = items.filter(a => a.end_date && a.end_date.slice(0, 10) < today);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">After-Sales Service</div>
            <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>AMC Contracts</h1><HelpButton moduleKey="amc" /></div>
            <p className="text-base text-slate-700 mt-1">{items.length} contracts · {expiring.length} expired · WhatsApp renewal reminders</p>
          </div>
          <button data-testid="amc-new-btn" onClick={() => setEditor({ customer_name: "", customer_phone: "", contract_type: "comprehensive", start_date: today, end_date: "", amount: 0, services_per_year: 4 })} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium inline-flex items-center gap-2"><Plus size={16} weight="bold" /> New AMC</button>
        </div>

        <div data-testid="amc-table" className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              {["AMC #", "Customer", "Type", "Start", "End", "Amount", "Status", "Actions"].map(h => <th key={h} className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={8} className="p-12 text-center text-slate-400 text-sm">No AMC contracts yet.</td></tr> :
              items.map((a, i) => {
                const isExpiring = a.end_date && a.end_date.slice(0, 10) < today;
                return (
                <tr key={a.amc_id} data-testid={`amc-row-${i}`} className={`border-b border-slate-100 hover:bg-slate-50/50 ${isExpiring ? "bg-rose-50/30" : ""}`}>
                  <td className="p-3 font-mono font-bold text-[#002FA7]">{a.amc_number}</td>
                  <td className="p-3 text-sm">{a.customer_name}</td>
                  <td className="p-3 text-xs uppercase tracking-wider">{a.contract_type}</td>
                  <td className="p-3 text-xs text-slate-500">{(a.start_date || "—").slice(0, 10)}</td>
                  <td className={`p-3 text-xs ${isExpiring ? "text-rose-700 font-bold" : "text-slate-500"}`}>{(a.end_date || "—").slice(0, 10)}</td>
                  <td className="p-3 text-sm tabular-nums">₹{Number(a.amount || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3"><span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${isExpiring ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{isExpiring ? "Expired" : a.status}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid={`amc-wa-renew-${i}`} onClick={() => openWa(a, "renewal_reminder")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-amber-300 text-amber-700 hover:bg-amber-50 font-bold inline-flex items-center gap-1"><WhatsappLogo size={12} weight="fill" />Renew</button>
                    <button data-testid={`amc-wa-service-${i}`} onClick={() => openWa(a, "service_schedule")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 ml-1 font-bold">Schedule</button>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editor && (
        <div className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center p-4" data-testid="amc-editor">
          <div className="bg-white border border-slate-200 w-full max-w-xl shadow-2xl">
            <div className="border-b px-5 py-3 flex items-center gap-2"><Wrench size={18} className="text-[#002FA7]" /><h3 className="font-semibold">New AMC Contract</h3></div>
            <div className="p-5 space-y-3">
              {[["customer_name", "Customer *"], ["customer_phone", "WhatsApp (91XXXXXXXXXX)"], ["customer_email", "Email"], ["start_date", "Start date *"], ["end_date", "End date *"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">{l}</label>
                  <input data-testid={`amc-fld-${k}`} value={editor[k] || ""} onChange={e => setEditor({...editor, [k]: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Amount (₹)</label>
                  <input data-testid="amc-fld-amount" type="number" value={editor.amount} onChange={e => setEditor({...editor, amount: Number(e.target.value)})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">Type</label>
                  <select value={editor.contract_type} onChange={e => setEditor({...editor, contract_type: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none">
                    {["comprehensive", "non_comprehensive"].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => setEditor(null)} className="rounded-none border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100">Cancel</button>
              <button data-testid="amc-save" onClick={save} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium">Save</button>
            </div>
          </div>
        </div>
      )}

      {wa && <WhatsAppSendModal open={true} onClose={() => setWa(null)} {...wa} user={user} />}
    </div>
  );
};

export default AMCContracts;
